import { msg } from '@lingui/core/macro';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useLocation } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import valueTypeValidator from 'modules/template-editor/domain/utils/form/template/value-type-validator';
import { createTemplate } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';

import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';

const PRESET: TemplatePresetsNameList = 'create';

function useCreateModal() {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const { templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const location = useLocation();
  const isCatalog = location.pathname.includes('catalog');

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!info) {
        throw Error('failed to create template: info is undefined');
      }
      const { width, height, name, visibility, access } = valueTypeValidator({
        values,
      });
      const resolution = { width: +width, height: +height };
      return dispatchWithToast(
        createTemplate({
          name,
          ownerId: info.id,
          resolution,
          createStartBoard: true,
          sportType: info.sportType,
          visibility,
          access,
        }),
        { error: msg`Не удалось создать шаблон` },
      );
    },
    [dispatchWithToast, info],
  );

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'create template') return;
    close();
    dispatch(resetPressedState());
  }, [isProcessed, isFinished, close, dispatch, currentProcess]);

  const initialValue = useMemo(
    () => ({
      width: '1920',
      height: '1080',
      name: '',
      visibility: getVisibilityOptions().find((option) => option.value === 'all') ?? getVisibilityOptions()[0],
      access: getAccessOptions().find((option) => option.value === (isCatalog ? 'default' : 'private')) ?? getAccessOptions()[0],
    }),
    [isCatalog],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin && isCatalog}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCreateModal;
